/**
 * Purchase journey feature types
 */

import type { Property } from '@/types';

// Journey steps
export type JourneyStep =
  | 'login'
  | 'welcome'
  | 'eligibility'
  | 'profile'
  | 'offers'
  | 'promissory'
  | 'rental'
  | 'keyPickup';

// Social status options
export type SocialStatus = 'single' | 'married' | 'divorced' | 'widowed';

// ID Information (read-only, from Nafath)
export interface IdInformation {
  fullName: string;
  dateOfBirth: string;
  placeOfBirth: string;
  socialStatus: SocialStatus;
  nationality: string;
  numberOfDependants: number;
  idType: string;
  idIssueDate: string;
  idExpiryDate: string;
  idIssuePlace: string;
  nationalAddress: string;
}

// Job Information (read-only, from GOSI)
export interface JobInformation {
  jobTitle: string;
  companyName: string;
  joiningDate: string;
  firstSalaryDate: string;
  yearsAtCurrentJob: number;
  totalExperienceYears: number;
  salaryDayOfMonth: number;
}

// Other Loans (read-only, from SIMAH)
export interface OtherLoans {
  personalLoan: number;
  realEstateLoan: number;
}

// Contact Information (editable)
export interface ContactInformation {
  phoneNumber: string;
  email: string;
}

// Emergency Contact (editable)
export interface EmergencyContact {
  name: string;
  phoneNumber: string;
  email: string;
}

// Monthly Expenses (editable)
export interface MonthlyExpenses {
  invoices: number;
  rent: number;
  workersSalary: number;
  transportation: number;
  education: number;
  health: number;
  insurance: number;
}

// Complete user profile
export interface UserProfile {
  idInformation: IdInformation;
  jobInformation: JobInformation;
  otherLoans: OtherLoans;
  contactInformation: ContactInformation;
  emergencyContact: EmergencyContact;
  monthlyExpenses: MonthlyExpenses;
}

// User data from Nafath authentication (basic info for welcome screen)
export interface NafahUserData {
  nationalId: string;
  firstName: string;
  fullName: string;
  // Full profile data populated after eligibility check
  profile?: UserProfile;
}

// Bank offer structure
export interface BankOffer {
  id: string;
  bankName: string;
  bankNameAr: string;
  logo?: string;
  interestRate: number;
  monthlyPayment: number;
  loanAmount: number;
  downPayment: number;
  tenure: number; // in years
  processingFee?: number;
  features?: string[];
}

// Eligibility check result
export interface EligibilityResult {
  isEligible: boolean;
  maxLoanAmount?: number;
  reasons?: string[];
  recommendations?: string[];
}

// Step component props
export interface StepContainerProps {
  children: React.ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  loadingMessage?: string;
}

export interface NafahLoginProps {
  onSuccess: (userData: NafahUserData) => void;
}

export interface WelcomeScreenProps {
  firstName: string;
  onContinue: () => void;
}

export interface EligibilityCheckProps {
  onComplete: (profile: UserProfile) => void;
}

export interface UserProfileFormProps {
  profile: UserProfile;
  onSubmit: (updatedProfile: UserProfile) => void;
}

export interface BankOffersProps {
  propertyPrice?: number;
  onSelect: (bank: BankOffer) => void;
}

export interface PromissoryNoteProps {
  bankOffer: BankOffer;
  userData: NafahUserData;
  onComplete: () => void;
}

export interface RentalAgreementProps {
  property?: Property;
  userData: NafahUserData;
  onComplete: () => void;
}

export interface KeyPickupProps {
  property?: Property;
  onComplete: () => void;
}

// Validation helpers
export const SOCIAL_STATUS_LABELS: Record<SocialStatus, string> = {
  single: 'أعزب',
  married: 'متزوج',
  divorced: 'مطلق',
  widowed: 'أرمل',
};

// Calculate age from date of birth
export function calculateAge(dateOfBirth: string): number {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}
